import { useState } from "react";
import type { FormEvent } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";

import {
  ALL_AUDIT_COLUMNS,
  AUDIT_COLUMN_OPTIONS,
  DENSITY_OPTIONS,
} from "./table-settings.constants";
import type { AuditColumnKey, AuditDensity, AuditTableSettings } from "./table-settings.constants";
import styles from "./table-settings-dialog.module.scss";

/**
 * Título do dialog no protótipo. O item do menu ⋮ que o abre continua sendo
 * "Configurações da Tabela", como nos Padrões Globais de Interface.
 */
export const TABLE_SETTINGS_TITLE = "Configuração da Tabela";

/** Liga o botão Aplicar, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "audit-table-settings-form";

interface TableSettingsDialogProps {
  /** Configuração em vigor na listagem; o modal abre com ela marcada. */
  settings: AuditTableSettings;
  /** Recebe a escolha confirmada em Aplicar; cabe ao chamador fechar o modal. */
  onApply: (settings: AuditTableSettings) => void;
  onCancel: () => void;
}

export function TableSettingsDialog({ settings, onApply, onCancel }: TableSettingsDialogProps) {
  const [density, setDensity] = useState<AuditDensity>(settings.density);
  const [columns, setColumns] = useState<AuditColumnKey[]>(settings.columns);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onApply({ density, columns });
  }

  /** Marcar de volta devolve a coluna à posição do protótipo, não ao fim da lista. */
  function toggleColumn(value: AuditColumnKey, checked: boolean) {
    setColumns((current) =>
      checked
        ? ALL_AUDIT_COLUMNS.filter((column) => column === value || current.includes(column))
        : current.filter((column) => column !== value),
    );
  }

  // O rodapé é uma faixa própria do Dialog, portanto fica fora do <form>:
  // o atributo `form` mantém Aplicar como botão de envio do formulário.
  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        onClick={onCancel}
        data-testid="audit-table-settings-cancel"
      />
      <Button
        type="submit"
        form={FORM_ID}
        className={styles.applyButton}
        label="Aplicar"
        data-testid="audit-table-settings-apply"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      contentClassName={styles.content}
      header={TABLE_SETTINGS_TITLE}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      dismissableMask
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="audit-table-settings-dialog"
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit}
        data-testid="audit-table-settings"
      >
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Tamanho da tabela</h3>

          <div className={styles.densityGroup} role="radiogroup" aria-label="Tamanho da tabela">
            {DENSITY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`${styles.densityOption} ${
                  density === option.value ? styles.densityOptionSelected : ""
                }`}
                htmlFor={`audit-density-${option.value}`}
              >
                <input
                  id={`audit-density-${option.value}`}
                  className={styles.srOnly}
                  type="radio"
                  name="audit-density"
                  value={option.value}
                  checked={density === option.value}
                  onChange={() => setDensity(option.value)}
                />
                <span className={styles.densityRadio} aria-hidden />
                <span className={styles.densityCopy}>
                  <span className={styles.densityLabel}>{option.label}</span>
                  <span className={styles.densityDescription}>{option.description}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Mostrar e Ocultar Colunas</h3>

          <div className={styles.columnList}>
            {AUDIT_COLUMN_OPTIONS.map((option) => (
              <div key={option.value} className={styles.columnOption}>
                <Checkbox
                  inputId={`audit-column-${option.value}`}
                  name="audit-columns"
                  value={option.value}
                  checked={columns.includes(option.value)}
                  onChange={(event) => toggleColumn(option.value, Boolean(event.checked))}
                />
                <label className={styles.columnLabel} htmlFor={`audit-column-${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </section>
      </form>
    </Dialog>
  );
}
